import { METRIC_FIELDS } from "./constants";
import type {
  BranchResultRow,
  ControlStockRow,
  DetailMetric,
  DetailTable,
  FamilyLineResultRow,
  MovementByDateRow,
  MovementDateMetric,
  MovementEntry,
  SummaryCard,
} from "./types";

export const buildFamilyLineResults = (
  filteredData: ControlStockRow[],
): FamilyLineResultRow[] => {
  const groups = new Map<string, FamilyLineResultRow>();
  filteredData.forEach((row) => {
    if (row.__isTotal) return;
    const familia = row.familia?.trim() || "Sin familia";
    const linea = row.linea?.trim() || "Sin línea";
    const key = `${familia}::${linea}`;
    const current =
      groups.get(key) ??
      {
        familia,
        linea,
        inventarioInicial: 0,
        compras: 0,
        remisionPositiva: 0,
        remisionNegativa: 0,
        ventas: 0,
        ajustePositivo: 0,
        ajusteNegativo: 0,
        teorico: 0,
        inventarioFinal: 0,
        diferencia: 0,
      };
    const unitValue = Number(row.valorUnitario ?? 0);
    current.inventarioInicial += Number(row.inventarioInicial ?? 0) * unitValue;
    current.compras += Number(row.compras ?? 0) * unitValue;
    current.remisionPositiva += Number(row.remisionPositiva ?? 0) * unitValue;
    current.remisionNegativa += Number(row.remisionNegativa ?? 0) * unitValue;
    current.ventas += Number(row.ventas ?? 0) * unitValue;
    current.ajustePositivo += Number(row.ajustePositivo ?? 0) * unitValue;
    current.ajusteNegativo += Number(row.ajusteNegativo ?? 0) * unitValue;
    current.teorico += Number(row.teorico ?? 0) * unitValue;
    current.inventarioFinal += Number(row.inventarioFinal ?? 0) * unitValue;
    current.diferencia += Number(row.diferencia ?? 0) * unitValue;
    groups.set(key, current);
  });
  return Array.from(groups.values()).sort((a, b) => {
    const family = a.familia.localeCompare(b.familia, "es");
    if (family !== 0) return family;
    return a.linea.localeCompare(b.linea, "es");
  });
};

export const buildBranchResults = (filteredData: ControlStockRow[]): BranchResultRow[] => {
  const groups = new Map<string, BranchResultRow>();
  const ensureBranch = (branchName: string) => {
    const current =
      groups.get(branchName) ??
      {
        sucursal: branchName,
        inventarioInicial: 0,
        compras: 0,
        remisionPositiva: 0,
        remisionNegativa: 0,
        ventas: 0,
        ajustePositivo: 0,
        ajusteNegativo: 0,
        teorico: 0,
        inventarioFinal: 0,
        diferencia: 0,
      };
    groups.set(branchName, current);
    return current;
  };

  const addAmount = (
    branchName: string,
    field:
      | "inventarioInicial"
      | "compras"
      | "remisionPositiva"
      | "remisionNegativa"
      | "ventas"
      | "ajustePositivo"
      | "ajusteNegativo"
      | "inventarioFinal",
    amount: number,
  ) => {
    const row = ensureBranch(branchName);
    row[field] += amount;
  };

  const metricFields: Array<
    | "inventarioInicial"
    | "compras"
    | "remisionPositiva"
    | "remisionNegativa"
    | "ventas"
    | "ajustePositivo"
    | "ajusteNegativo"
    | "inventarioFinal"
  > = [
    "inventarioInicial",
    "compras",
    "remisionPositiva",
    "remisionNegativa",
    "ventas",
    "ajustePositivo",
    "ajusteNegativo",
    "inventarioFinal",
  ];

  filteredData.forEach((row) => {
    if (row.__isTotal) return;
    const fallbackBranch = row.sucursal?.trim() || "Sin sucursal";
    const unitValue = Number(row.valorUnitario ?? 0);

    metricFields.forEach((field) => {
      const details = row.__details?.[field] as MovementEntry[] | undefined;
      if (Array.isArray(details) && details.length > 0) {
        details.forEach((entry) => {
          const branchFromDetail = entry.deposito?.trim() || fallbackBranch;
          const qty = Number(entry.cantidad ?? 0);
          const unitFromDetail = Number(entry.precioUnitario ?? unitValue);
          addAmount(branchFromDetail, field, qty * unitFromDetail);
        });
        return;
      }
      addAmount(
        fallbackBranch,
        field,
        Number(row[field] ?? 0) * unitValue,
      );
    });
  });

  groups.forEach((item) => {
    item.teorico =
      item.inventarioInicial +
      item.compras +
      item.remisionPositiva -
      item.remisionNegativa -
      item.ventas +
      item.ajustePositivo -
      item.ajusteNegativo;
    item.diferencia = item.inventarioFinal - item.teorico;
  });

  return Array.from(groups.values()).sort((a, b) =>
    a.sucursal.localeCompare(b.sucursal, "es"),
  );
};

export const buildMovementDateColumns = (startDate: string, endDate: string) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return [] as string[];
  }
  const result: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    result.push(cursor.toISOString().slice(0, 10));
    cursor.setDate(cursor.getDate() + 1);
    if (result.length > 120) {
      break;
    }
  }
  return result;
};

export const buildMovementRowsForMetric = (
  filteredData: ControlStockRow[],
  metric: MovementDateMetric,
  endDate: string,
): MovementByDateRow[] => {
  const rowsMap = new Map<string, MovementByDateRow>();
  const normalizeDate = (value?: string | null) => {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed.toISOString().slice(0, 10);
  };
  const ensureRow = (row: ControlStockRow) => {
    const key = `${row.server}::${row.sucursal}::${row.idArticulo}`;
    let current = rowsMap.get(key);
    if (!current) {
      current = {
        idArticulo: row.idArticulo,
        articulo: row.articulo,
        unidadMedida: row.unidadMedida,
        totalCantidad: 0,
        totalMonto: 0,
        porFecha: {},
      };
      rowsMap.set(key, current);
    }
    return current;
  };
  const addValue = (
    current: MovementByDateRow,
    fecha: string | null,
    cantidad: number,
    precioUnitario: number,
  ) => {
    const dateKey = normalizeDate(fecha);
    if (!dateKey) return;
    const monto = cantidad * precioUnitario;
    const slot = current.porFecha[dateKey] ?? { cantidad: 0, monto: 0 };
    slot.cantidad += cantidad;
    slot.monto += monto;
    current.porFecha[dateKey] = slot;
    current.totalCantidad += cantidad;
    current.totalMonto += monto;
  };
  filteredData.forEach((row) => {
    if (row.__isTotal) return;
    const current = ensureRow(row);
    if (metric === "compras") {
      const details = row.__details?.compras as MovementEntry[] | undefined;
      if (Array.isArray(details) && details.length > 0) {
        details.forEach((entry) =>
          addValue(
            current,
            entry.fecha,
            Number(entry.cantidad ?? 0),
            Number(entry.precioUnitario ?? row.valorUnitario ?? 0),
          ),
        );
        return;
      }
      if (Number(row.compras ?? 0) !== 0) {
        addValue(current, endDate, Number(row.compras ?? 0), Number(row.valorUnitario ?? 0));
      }
      return;
    }
    if (metric === "ventas") {
      const details = row.__details?.ventas as MovementEntry[] | undefined;
      if (Array.isArray(details) && details.length > 0) {
        details.forEach((entry) =>
          addValue(
            current,
            entry.fecha,
            Number(entry.cantidad ?? 0),
            Number(entry.precioUnitario ?? row.valorUnitario ?? 0),
          ),
        );
        return;
      }
      if (Number(row.ventas ?? 0) !== 0) {
        addValue(current, endDate, Number(row.ventas ?? 0), Number(row.valorUnitario ?? 0));
      }
      return;
    }
    if (metric === "mermas") {
      const details = row.__details?.ajusteNegativo as MovementEntry[] | undefined;
      if (Array.isArray(details) && details.length > 0) {
        details.forEach((entry) =>
          addValue(
            current,
            entry.fecha,
            Number(entry.cantidad ?? 0),
            Number(entry.precioUnitario ?? row.valorUnitario ?? 0),
          ),
        );
        return;
      }
      if (Number(row.ajusteNegativo ?? 0) !== 0) {
        addValue(
          current,
          endDate,
          Number(row.ajusteNegativo ?? 0),
          Number(row.valorUnitario ?? 0),
        );
      }
      return;
    }
    const detailsPos = row.__details?.remisionPositiva as MovementEntry[] | undefined;
    const detailsNeg = row.__details?.remisionNegativa as MovementEntry[] | undefined;
    let hasDetail = false;
    if (Array.isArray(detailsPos) && detailsPos.length > 0) {
      hasDetail = true;
      detailsPos.forEach((entry) =>
        addValue(
          current,
          entry.fecha,
          Number(entry.cantidad ?? 0),
          Number(entry.precioUnitario ?? row.valorUnitario ?? 0),
        ),
      );
    }
    if (Array.isArray(detailsNeg) && detailsNeg.length > 0) {
      hasDetail = true;
      detailsNeg.forEach((entry) =>
        addValue(
          current,
          entry.fecha,
          -Number(entry.cantidad ?? 0),
          Number(entry.precioUnitario ?? row.valorUnitario ?? 0),
        ),
      );
    }
    if (!hasDetail) {
      const neto = Number(row.remisionPositiva ?? 0) - Number(row.remisionNegativa ?? 0);
      if (neto !== 0) {
        addValue(current, endDate, neto, Number(row.valorUnitario ?? 0));
      }
    }
  });
  return Array.from(rowsMap.values())
    .filter((row) => row.totalCantidad !== 0 || row.totalMonto !== 0)
    .sort((a, b) => a.articulo.localeCompare(b.articulo, "es"));
};

export const buildMovementRowsByDate = (
  filteredData: ControlStockRow[],
  movementDateMetric: MovementDateMetric,
  endDate: string,
) => buildMovementRowsForMetric(filteredData, movementDateMetric, endDate);

export const buildMovementDateAvailability = (
  filteredData: ControlStockRow[],
): Record<MovementDateMetric, boolean> => {
  const hasMetricData = (
    row: ControlStockRow,
    metric: MovementDateMetric,
  ) => {
    if (metric === "compras") {
      const details = row.__details?.compras as MovementEntry[] | undefined;
      return (
        (Array.isArray(details) && details.some((entry) => Number(entry.cantidad ?? 0) !== 0)) ||
        Number(row.compras ?? 0) !== 0
      );
    }
    if (metric === "ventas") {
      const details = row.__details?.ventas as MovementEntry[] | undefined;
      return (
        (Array.isArray(details) && details.some((entry) => Number(entry.cantidad ?? 0) !== 0)) ||
        Number(row.ventas ?? 0) !== 0
      );
    }
    if (metric === "mermas") {
      const details = row.__details?.ajusteNegativo as MovementEntry[] | undefined;
      return (
        (Array.isArray(details) && details.some((entry) => Number(entry.cantidad ?? 0) !== 0)) ||
        Number(row.ajusteNegativo ?? 0) !== 0
      );
    }
    const detailsPos = row.__details?.remisionPositiva as MovementEntry[] | undefined;
    const detailsNeg = row.__details?.remisionNegativa as MovementEntry[] | undefined;
    return (
      (Array.isArray(detailsPos) && detailsPos.some((entry) => Number(entry.cantidad ?? 0) !== 0)) ||
      (Array.isArray(detailsNeg) && detailsNeg.some((entry) => Number(entry.cantidad ?? 0) !== 0)) ||
      Number(row.remisionPositiva ?? 0) !== 0 ||
      Number(row.remisionNegativa ?? 0) !== 0
    );
  };

  return {
    compras: filteredData.some((row) => hasMetricData(row, "compras")),
    remisiones: filteredData.some((row) => hasMetricData(row, "remisiones")),
    ventas: filteredData.some((row) => hasMetricData(row, "ventas")),
    mermas: filteredData.some((row) => hasMetricData(row, "mermas")),
  };
};

export const buildSummaryCards = (filteredData: ControlStockRow[]): SummaryCard[] => {
  const hasRows = filteredData.length > 0;
  const sumAmount = (field: keyof ControlStockRow) =>
    filteredData.reduce((acc, row) => {
      const qty = Number(row[field] ?? 0);
      const unit = Number(row.valorUnitario ?? 0);
      return acc + qty * unit;
    }, 0);

  const diff = sumAmount("diferencia");
  const inventarioFinalMonto = sumAmount("inventarioFinal");
  const inventarioInicialMonto = sumAmount("inventarioInicial");
  const comprasMonto = sumAmount("compras");
  const ventasMonto = sumAmount("ventas");
  const remisionesMonto =
    sumAmount("remisionPositiva") - sumAmount("remisionNegativa");
  const remisionesCantidad = filteredData.reduce((acc, row) => {
    const pos = Number(row.remisionPositiva ?? 0);
    const neg = Number(row.remisionNegativa ?? 0);
    return acc + pos + neg;
  }, 0);
  const formatHelper = (value: number) =>
    `${value.toLocaleString("es-PY", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} ítems`;

  return [
    {
      id: "inv-inicial",
      label: "Inventario inicial (Gs.)",
      value: inventarioInicialMonto,
      icon: "ri-archive-line",
      iconColor: "warning",
      trendIcon: "ri-timer-2-line text-warning",
      helper: hasRows
        ? formatHelper(
            filteredData.reduce(
              (acc, row) => acc + Number(row.inventarioInicial ?? 0),
              0,
            ),
          )
        : "Sin datos",
    },
    {
      id: "compras",
      label: "Compras (Gs.)",
      value: comprasMonto,
      icon: "ri-shopping-bag-3-line",
      iconColor: "info",
      trendIcon: "ri-arrow-left-down-line text-info",
      helper: hasRows
        ? formatHelper(
            filteredData.reduce(
              (acc, row) => acc + Number(row.compras ?? 0),
              0,
            ),
          )
        : "Sin datos",
    },
    {
      id: "remisiones",
      label: "Remisiones (Gs.)",
      value: remisionesMonto,
      icon: "ri-exchange-line",
      iconColor: "secondary",
      trendIcon: "ri-swap-line text-secondary",
      helper: hasRows
        ? `${remisionesCantidad.toLocaleString("es-PY")} ítems`
        : "Sin datos",
    },
    {
      id: "ventas",
      label: "Ventas por consumo (Gs.)",
      value: ventasMonto,
      icon: "ri-shopping-cart-2-line",
      iconColor: "danger",
      trendIcon: "ri-arrow-right-up-line text-danger",
      helper: hasRows
        ? formatHelper(
            filteredData.reduce(
              (acc, row) => acc + Number(row.ventas ?? 0),
              0,
            ),
          )
        : "Sin datos",
    },
    {
      id: "inv-final",
      label: "Inventario final (Gs.)",
      value: inventarioFinalMonto,
      icon: "ri-inbox-archive-line",
      iconColor: "primary",
      trendIcon: "ri-archive-drawer-line text-primary",
      helper: hasRows
        ? formatHelper(
            filteredData.reduce(
              (acc, row) => acc + Number(row.inventarioFinal ?? 0),
              0,
            ),
          )
        : "Sin datos",
    },
    {
      id: "diff",
      label: "Total diferencia (Gs.)",
      value: diff,
      icon: "ri-exchange-dollar-line",
      iconColor: diff >= 0 ? "success" : "danger",
      trendIcon:
        diff >= 0
          ? "ri-arrow-up-circle-line text-success"
          : "ri-arrow-down-circle-line text-danger",
      helper: hasRows ? `Artículos: ${filteredData.length}` : "Sin datos",
    },
  ];
};

export const buildTotalsRow = (
  filteredData: ControlStockRow[],
  metricMode: "cantidad" | "monto" | "ambos",
): ControlStockRow | null => {
  if ((metricMode === "cantidad") || filteredData.length === 0) {
    return null;
  }
  const totalsQty: Record<string, number> = {};
  const totalsAmount: Record<string, number> = {};
  METRIC_FIELDS.forEach((field) => {
    totalsQty[field] = 0;
    totalsAmount[field] = 0;
  });

  filteredData.forEach((row) => {
    METRIC_FIELDS.forEach((field) => {
      const qtyValue = Number(row[field] ?? 0);
      totalsQty[field] += qtyValue;
      totalsAmount[field] += qtyValue * row.valorUnitario;
    });
  });

  return {
    idArticulo: "TOTAL",
    articulo: "",
    server: "",
    sucursal: "",
    fechaReferencia: "",
    unidadMedida: "",
    familia: null,
    linea: null,
    valorUnitario: 1,
    inventarioInicial: totalsQty.inventarioInicial,
    compras: totalsQty.compras,
    remisionPositiva: totalsQty.remisionPositiva,
    remisionNegativa: totalsQty.remisionNegativa,
    ventas: totalsQty.ventas,
    ajustePositivo: totalsQty.ajustePositivo,
    ajusteNegativo: totalsQty.ajusteNegativo,
    teorico: totalsQty.teorico,
    inventarioFinal: totalsQty.inventarioFinal,
    diferencia: totalsQty.diferencia,
    __isTotal: true,
    __isGrandTotal: true,
    __amountTotals: totalsAmount,
    __familiaId: null,
    __lineaId: null,
  };
};

export const buildDetailTable = (
  filteredData: ControlStockRow[],
  detailsMetric: DetailMetric,
): DetailTable => {
  const rows: DetailTable["rows"] = [];

  filteredData.forEach((row) => {
    if (
      row.__isTotal ||
      row.__isGrandTotal ||
      row.__isCollectionHeader ||
      row.__isCollectionSubtotal
    ) {
      return;
    }

    const metricQty = Number(row[detailsMetric] ?? 0);
    const fallbackUnitPrice = Number(row.valorUnitario ?? 0);
    const details = row.__details?.[detailsMetric] as MovementEntry[] | undefined;

    let hasValidDetails = false;

    if (Array.isArray(details) && details.length > 0) {
      details.forEach((detail) => {
        const qty = Number(detail.cantidad ?? 0);
        const rawDetailUnitPrice = Number(detail.precioUnitario);
        const unitPrice =
          Number.isFinite(rawDetailUnitPrice) && rawDetailUnitPrice > 0
            ? rawDetailUnitPrice
            : fallbackUnitPrice;

        if (qty === 0 && unitPrice === 0) {
          return;
        }

        hasValidDetails = true;
        rows.push({
          idArticulo: row.idArticulo,
          articulo: row.articulo,
          deposito: detail.deposito ?? row.sucursal ?? null,
          cantidad: qty,
          monto: qty * unitPrice,
          fecha: detail.fecha ?? row.fechaReferencia,
          precioUnitario: unitPrice,
          idMovimiento: detail.idMovimiento ?? "-",
          documento: detail.documento ?? "-",
          idArticuloVenta: detail.idArticuloVenta ?? null,
          articuloVenta: detail.articuloVenta ?? null,
          idReceta: detail.idReceta ?? null,
          receta: detail.receta ?? null,
          cantidadVendida: detail.cantidadVendida ?? null,
        });
      });
    }

    if (hasValidDetails || metricQty === 0) {
      return;
    }

    rows.push({
      idArticulo: row.idArticulo,
      articulo: row.articulo,
      deposito: row.sucursal ?? null,
      cantidad: metricQty,
      monto: metricQty * fallbackUnitPrice,
      fecha: row.fechaReferencia,
      precioUnitario: fallbackUnitPrice,
      idMovimiento: "-",
      documento: "-",
      idArticuloVenta: null,
      articuloVenta: null,
      idReceta: null,
      receta: null,
      cantidadVendida: null,
    });
  });

  const totals = rows.reduce(
    (acc, row) => {
      acc.cantidad += row.cantidad;
      acc.monto += row.monto;
      return acc;
    },
    { cantidad: 0, monto: 0 },
  );
  return { rows, totals };
};
